import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Eye, EyeOff, Lock, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// We need to receive useAuth as a prop or from App.js's export
// For now, we'll create a local apiClient and handle auth locally
const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [trade, setTrade] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [companyBranding, setCompanyBranding] = useState(null);
  const navigate = useNavigate();

  // Trade options
  const tradeOptions = [
    "Concrete Coatings & Epoxy",
    "Landscaping",
    "General Contractor",
    "Electrical",
    "Plumbing",
    "HVAC",
    "Roofing",
    "Painting",
    "Flooring",
    "Carpentry",
    "Masonry",
    "Drywall",
    "Pressure Washing",
    "Pool Service",
    "Fencing",
    "Tree Service",
    "Pest Control",
    "Cleaning Services",
    "Handyman",
    "Other"
  ];

  // Login function
  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    // Clear any stale impersonation tokens on fresh login
    localStorage.removeItem("original_admin_token");
    localStorage.removeItem("original_admin_user");
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    localStorage.setItem("lastActivity", Date.now().toString());
    return res.data;
  };

  // Request account function
  const requestAccount = async (data) => {
    const res = await apiClient.post("/auth/request-account", data);
    return res.data;
  };

  // Fetch company branding when email changes (debounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (email && email.includes('@') && !isRegister) {
        try {
          const res = await axios.get(`${API}/company-branding-by-email?email=${encodeURIComponent(email)}`);
          if (res.data) {
            setCompanyBranding(res.data);
          } else {
            setCompanyBranding(null);
          }
        } catch {
          setCompanyBranding(null);
        }
      } else {
        setCompanyBranding(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [email, isRegister]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        // Request account instead of immediate registration
        // Auto-detect the requester's IANA timezone so the company is provisioned
        // on the correct local clock (no surprise "8 AM Central" emails for
        // Phoenix or California tenants).
        const browserTz = (() => {
          try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; }
          catch { return null; }
        })();
        await requestAccount({ 
          email, 
          name, 
          company_name: company,
          phone: phone || null,
          trade: trade || null,
          message: message || null,
          timezone: browserTz,
        });
        setRequestSubmitted(true);
        toast.success("Account request submitted! You'll receive an email once approved.");
      } else {
        const result = await login(email, password);
        toast.success("Welcome back!");
        // Redirect based on user role - use window.location for full page reload
        // This ensures the App's AuthProvider picks up the new user state.
        // Platform admins land on /platform (their scope only) instead of
        // /dashboard — /dashboard is tenant-scoped and would leak cross-
        // tenant aggregate data to a null-company_id user (Feb 2026 fix).
        if (result.user.role === 'employee') {
          window.location.href = "/my-jobs";
        } else if (result.user.role === 'platform_admin') {
          window.location.href = "/platform";
        } else {
          window.location.href = "/dashboard";
        }
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  };

  // Show success message after request submitted
  if (requestSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-8">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-white mb-4">Request Submitted!</h2>
          <p className="text-zinc-400 mb-6">
            Your account request has been sent to our admin team for review. 
            You'll receive an email at <span className="text-primary font-medium">{email}</span> once your account is approved.
          </p>
          <Button
            onClick={() => {
              setRequestSubmitted(false);
              setIsRegister(false);
              setEmail("");
              setName("");
              setCompany("");
              setPhone("");
              setTrade("");
              setMessage("");
            }}
            className="bg-primary text-black font-heading font-bold"
          >
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col lg:flex-row bg-zinc-950">
      {/* Hero Section - Hidden on mobile.
          Feb 27 2026: full-panorama layout — the entire image is visible
          (jobsite + crew + Main Street shops) with a blurred copy of the
          same image filling the letterbox top/bottom bars so the panel
          feels seamless instead of black-bar-boxed. The main image is
          `object-contain object-center` so nothing gets cropped. */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden bg-zinc-950">
        {/* Blurred backdrop — same image, scaled up + blurred to fill the
            letterbox areas top and bottom. object-cover ensures no gaps. */}
        <img
          aria-hidden="true"
          src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cecbd948-aa1c-4ce7-a21f-9fd06d0fb078/artifacts/cjis2gq9_CrewSync%20pro%20Login%20screen.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40"
        />
        {/* Sharp foreground — full image, uncropped, centered. */}
        <img
          src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cecbd948-aa1c-4ce7-a21f-9fd06d0fb078/artifacts/cjis2gq9_CrewSync%20pro%20Login%20screen.webp"
          alt="A diverse crew of tradespeople and small-business owners on a jobsite and Main Street"
          className="relative w-full h-full object-contain object-center"
        />
        {/* Bottom gradient for headline legibility. */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
        <div className="absolute inset-0 z-10 flex flex-col justify-end p-12 pointer-events-none">
          <h1 className="font-heading text-5xl font-bold text-white uppercase tracking-tight mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            Know Your Numbers.<br />Grow With Confidence.
          </h1>
          <p className="text-zinc-100 text-lg max-w-md drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
            The complete field service management platform to manage your team, jobs, and finances — built for contractors, by contractors.
          </p>
        </div>
      </div>

      {/* Login Form Section - Compact on mobile */}
      <div className="w-full lg:w-2/5 flex-1 flex items-center justify-center overflow-y-auto">
        <div className="w-full max-w-md px-6 py-6 sm:px-8 sm:py-8">
          {/* Platform Logo — CrewSync Pro only. Tenant logos are no longer
              shown on the platform chrome; they remain on customer-facing
              outputs (emails, PDFs, share links). */}
          <div className="flex flex-col sm:flex-row items-center gap-3 mb-4 sm:mb-6">
            <div className="flex items-start">
              <img src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" alt="CrewSync Pro" className="h-10 sm:h-14 w-auto" />
              <span className="text-xs text-zinc-400 font-medium mt-0.5">™</span>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2 sm:mb-3 text-[10px] sm:text-xs text-zinc-500">
            <Lock className="w-3 h-3 text-green-500" />
            <span>Secure Connection • 256-bit SSL Encryption</span>
          </div>

          <h2 className="text-xl sm:text-2xl font-heading font-bold mb-1 uppercase tracking-tight text-center sm:text-left">
            {isRegister ? "CREATE ACCOUNT" : "WELCOME BACK"}
          </h2>
          <p className="text-zinc-400 mb-4 sm:mb-5 text-xs sm:text-sm text-center sm:text-left">
            {isRegister ? "Start managing your business today" : "Sign in to continue to your dashboard"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            {isRegister && (
              <>
                <div>
                  <Label htmlFor="name" className="text-xs sm:text-sm">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    required
                    className="mt-1 h-10 sm:h-11 bg-zinc-900 border-zinc-800 text-sm"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-xs sm:text-sm">Company Name</Label>
                  <Input
                    id="company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Smith Landscaping"
                    required
                    className="mt-1 h-10 sm:h-11 bg-zinc-900 border-zinc-800 text-sm"
                    data-testid="register-company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs sm:text-sm">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1 h-10 sm:h-11 bg-zinc-900 border-zinc-800 text-sm"
                    data-testid="register-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="trade" className="text-xs sm:text-sm">Trade / Industry</Label>
                  <select
                    id="trade"
                    value={trade}
                    onChange={(e) => setTrade(e.target.value)}
                    className="mt-1 w-full h-10 sm:h-11 bg-zinc-900 border border-zinc-800 text-white text-sm rounded-md px-3"
                    data-testid="register-trade-select"
                  >
                    <option value="">Select your trade...</option>
                    {tradeOptions.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email" className="text-xs sm:text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1 h-10 sm:h-11 bg-zinc-900 border-zinc-800 text-sm"
                data-testid="email-input"
              />
            </div>
            {!isRegister && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs sm:text-sm">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-10 sm:h-11 bg-zinc-900 border-zinc-800 pr-10 text-sm"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
            {isRegister && (
              <div>
                <Label htmlFor="message" className="text-xs sm:text-sm">Message (Optional)</Label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us about your business..."
                  rows={2}
                  className="mt-1 w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                  data-testid="register-message-input"
                />
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 sm:h-11 bg-primary text-black font-heading font-bold uppercase tracking-wider hover:bg-primary/90 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all text-sm"
              data-testid="submit-btn"
            >
              {loading ? "Please wait..." : isRegister ? "Request Access" : "Sign In"}
            </Button>
          </form>

          {isRegister && (
            <div className="mt-3 text-center">
              <p className="text-zinc-500 text-[10px] sm:text-xs">
                Your request will be reviewed by our admin team. You'll receive an email once approved.
              </p>
              <p className="mt-2 text-yellow-500/80 text-[10px] sm:text-xs flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" />
                Due to security checks and demand for trials, expect access within 24 hours.
              </p>
            </div>
          )}

          <p className="mt-4 text-center text-zinc-400 text-xs sm:text-sm">
            {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-primary hover:underline font-medium"
              data-testid="toggle-auth-btn"
            >
              {isRegister ? "Sign In" : "Request Access"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
